import type { ExtraFile } from '@devlearn/shared';
import { useState } from 'react';
import { storage } from '@/lib/storage';
import { codeKey } from './useAutosave';

/**
 * Extra challenge files (chapter 6). Editable ones are autosaved per file under
 * `devlearn:code:<id>:v<version>:<file>` so learner edits survive a reload; read-only ones always use the content.
 */
export function useExtraFiles(challengeId: string, contentVersion: number, files: ExtraFile[] = []) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      files.map((f) => [
        f.name,
        f.editable ? (storage.get(codeKey(challengeId, contentVersion, f.name)) ?? f.code) : f.code,
      ]),
    ),
  );

  function update(name: string, code: string) {
    const file = files.find((f) => f.name === name);
    if (!file?.editable) return;
    storage.set(codeKey(challengeId, contentVersion, name), code);
    setValues((v) => ({ ...v, [name]: code }));
  }

  function reset() {
    for (const f of files) if (f.editable) storage.remove(codeKey(challengeId, contentVersion, f.name));
    setValues(Object.fromEntries(files.map((f) => [f.name, f.code])));
  }

  return { values, update, reset };
}
